// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import boxShadows from "assets/theme/base/boxShadows";

// Soft UI Dashboard PRO React helper functions
import pxToRem from "assets/theme/functions/pxToRem";
import boxShadow from "assets/theme/functions/boxShadow";

// @emotion/react components
import { keyframes } from "@emotion/react";

import { THEME_TYPES, THEME_COLORS } from "constants/theme";
import { useSelector } from "react-redux";

export default (selectSize, selectError, selectSuccess) => {
  const { white, text, grey, inputColors, gradients, transparent } = colors;
  const { size, fontWeightLight } = typography;
  const { borderWidth, borderRadius } = borders;
  const { inputBoxShadow, lg } = boxShadows;
  const isDark = useSelector((store) => store?.portal?.settings.themeMode) === THEME_TYPES.DARK;
  const defaultBorderColor = isDark ? "var(--input-border-color)" : inputColors.borderColor.main;
  const disabledBackground = isDark ? "var(--input-bg-color)" : grey["200"];
  const disabledBorderColor = isDark ? "var(--input-border-color)" : inputColors.borderColor.main;
  const controlBackground = isDark ? "var(--input-bg-color)" : white.main;
  const controlTextColor = isDark ? white.main : text.main;
  const disabledTextColor = isDark ? grey[500] : grey[600];

  let borderColorValue;
  let borderColorFocused;
  let boxShadowValue;
  let selectSizeValue;

  if (selectError) {
    borderColorValue = defaultBorderColor;
    borderColorFocused = inputColors.borderColor.focus;
  } else if (selectSuccess) {
    borderColorValue = inputColors.success;
    borderColorFocused = inputColors.success;
  } else {
    borderColorValue = defaultBorderColor;
    borderColorFocused = inputColors.borderColor.focus;
  }

  if (selectError) {
    boxShadowValue = boxShadow([0, 0], [0, 2], inputColors.boxShadow, 1);
  } else if (selectSuccess) {
    boxShadowValue = inputBoxShadow.success;
  } else {
    boxShadowValue = boxShadow([0, 0], [0, 2], inputColors.boxShadow, 1);
  }

  if (selectSize === "small") {
    selectSizeValue = pxToRem(32);
  } else if (selectSize === "large") {
    selectSizeValue = pxToRem(48);
  } else {
    selectSizeValue = pxToRem(40);
  }

  // animations
  const prespective = keyframes`
    from {
      opacity: 0;
      transform: perspective(999px) rotateX(-10deg) translateZ(0) translate3d(0, 0, 0);
    }
    to {
      opacity: 1;
      transform: perspective(999px) rotateX(0deg) translateZ(0) translate3d(0, 0, 5px);
    }
  `;

  return {
    container: (provided, state) => {
      const isEditorSelect =
        state.selectProps.className &&
        state.selectProps.className.includes("editor-language-select");

      return {
        ...provided,
        ...(isEditorSelect && {
          width: pxToRem(115),
          minWidth: pxToRem(85),
        }),
      };
    },

    control: (provided, state) => ({
      ...provided,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      width: "100%",
      minHeight: selectSizeValue,
      height: state.hasValue && state.selectProps.isMulti ? "auto" : selectSizeValue,
      padding: 0,
      fontSize: selectSize === "small" ? size.xs : size.sm,
      fontWeight: fontWeightLight,
      backgroundColor: state.isDisabled ? disabledBackground : controlBackground,
      backgroundClip: "padding-box",
      border: `${borderWidth[1]} solid ${defaultBorderColor}`,
      appearance: "none",
      borderRadius: borderRadius.md,
      transition: "box-shadow 150ms ease, border-color 150ms ease",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      borderColor: state.isDisabled
        ? disabledBorderColor
        : state.isFocused
        ? borderColorFocused
        : borderColorValue,
      boxShadow: state.isDisabled ? "none" : state.isFocused ? boxShadowValue : "none",
      overflow: state.selectProps.isMulti ? "hidden" : "visible",

      "&:hover": {
        borderColor: state.isDisabled
          ? disabledBorderColor
          : state.isFocused
          ? borderColorFocused
          : borderColorValue,
      },
    }),

    valueContainer: (provided, state) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      gap: 2,
      width: "100%",
      flexWrap: state.selectProps.isMulti ? "wrap" : "nowrap",
      minHeight: selectSizeValue,
      height: "auto",
      padding: `0 ${pxToRem(12)}`,
      color: state.isDisabled ? disabledTextColor : controlTextColor,
      fontWeight: fontWeightLight,
      overflow: state.selectProps.isMulti ? "hidden" : "visible",
      "&::-webkit-scrollbar": {
        width: "4px",
        height: "4px",
        marginTop: "8px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#ccc",
        borderRadius: "4px",
      },
    }),

    placeholder: (provided, state) => ({
      ...provided,
      position: "absolute",
      marginLeft: 0,
      marginRight: 0,
      opacity: 1,
      top: state.hasValue || state.selectProps.inputValue ? -18 : "25%",
      fontSize: "14px",
      transition: "top 0.1s, font-size 0.1s",
      color: isDark ? white.placeholder : "black",
      fontWeight: `${fontWeightLight} !important`,
    }),

    singleValue: (provided, state) => ({
      ...provided,
      position: "absolute",
      top: selectSize === "small" ? "52%" : "51%",
      transform: "translateY(-50%)",
      color: state.isDisabled ? disabledTextColor : controlTextColor,
      fontWeight: fontWeightLight,
    }),

    input: (provided) => ({
      ...provided,
      color: controlTextColor,
      fontWeight: fontWeightLight,
      position: "relative",
      margin: 0,
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    dropdownIndicator: (provided, state) => ({
      display: state.isDisabled ? "none" : "flex",
      padding: pxToRem(12),

      "& svg": {
        fill: isDark ? white.main : text.main,
        width: pxToRem(14),
        height: pxToRem(14),
        strokeWidth: 1,
        stroke: isDark ? white.main : text.main,
        transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0)",
        transition: "transform 250ms ease",
      },
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 1400,
      position: "fixed",
    }),

    menu: (provided) => ({
      ...provided,
      minWidth: pxToRem(50),
      boxShadow: lg,
      padding: `${pxToRem(16)} ${pxToRem(8)}`,
      fontSize: size.sm,
      textAlign: "left",
      animation: `${prespective} 250ms ease forwards !important`,
      transformOrigin: "50% 0",
      borderRadius: borderRadius.md,
      backgroundColor: isDark ? THEME_COLORS.DARK.INPUT_BG : THEME_COLORS.LIGHT.INPUT_BG,
      color: isDark ? white.main : text.main,
      zIndex: 1401,
    }),

    menuList: (provided) => ({
      ...provided,
      maxHeight: 150,
      padding: 0,
      backgroundColor: isDark ? THEME_COLORS.DARK.INPUT_BG : THEME_COLORS.LIGHT.INPUT_BG,
    }),

    option: (provided, state) => {
      const selectedBg = isDark ? THEME_COLORS.DARK.PRIMARY_BG : THEME_COLORS.LIGHT.SELECTED_BG;
      const hoverBg = isDark ? THEME_COLORS.DARK.PRIMARY_BG : THEME_COLORS.LIGHT.SELECTED_BG;
      const textColor = isDark ? THEME_COLORS.DARK.PRIMARY_FONT : THEME_COLORS.LIGHT.PRIMARY_FONT;

      return {
        ...provided,
        position: "relative",
        minHeight: "unset",
        borderRadius: borderRadius.md,
        fontSize: "12px",
        fontWeight: fontWeightLight,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: state.isDisabled ? 0.5 : 1,
        userSelect: state.isDisabled ? "none" : "auto",
        transition: "background-color 300ms ease, color 300ms ease",
        backgroundColor: state.isSelected
          ? selectedBg
          : state.isFocused
          ? hoverBg
          : "transparent",
        color: textColor,

        "&:after": {
          content: "' '",
          display: "block",
          fontSize: size.xs,
          position: "absolute",
          right: pxToRem(10),
          top: "52%",
          color: textColor,
          opacity: 0,
          transform: "translateY(-50%)",
          transition: "opacity 300ms ease",
        },

        "&:hover, &:focus": {
          backgroundColor: state.isSelected ? selectedBg : hoverBg,
          color: textColor,
        },
      };
    },

    multiValue: (provided) => ({
      ...provided,
      margin: 0,
      marginRight: pxToRem(4),
      borderRadius: borderRadius.section,
      display: "flex",
      alignItems: "center",
      maxWidth: "100%",
      backgroundColor: gradients.dark.state,
      color: white.main,
      padding: `${pxToRem(3)} ${pxToRem(2)} ${pxToRem(2)} ${pxToRem(6)}`,
    }),

    multiValueLabel: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      minHeight: pxToRem(20),
      maxWidth: pxToRem(120),
      padding: 0,
      fontSize: size.sm,
      color: white.main,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),

    multiValueRemove: (provided) => ({
      ...provided,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0.8,
      color: white.main,

      "&:hover": {
        backgroundColor: transparent.main,
        color: white.main,
      },
    }),
  };
};
